/**
 * Property catalog — the on-site visit history the portal shows per property,
 * plus the ordering rule for deployed solutions (which come from `deployments`).
 *
 * Both used to live in `src/data/propertySolutions.ts` and
 * `src/data/propertyTrips.ts`, which meant a customer read values that traced to
 * a commit rather than to a row, and staff could not correct a word without a
 * release. They now come from `property_directory` (narrative, aliases) and
 * `property_visit_history` (visits), read through their customer views.
 *
 * Everything here is pure: shapes, parsing and lookup. The fetch lives in
 * `hooks/usePropertyCatalog.ts`.
 */
import type { PropertyDirectoryEntry } from './propertyDirectory';

/** `solution_type` enum labels (20260402000002_enums.sql). */
export type SolutionType =
  | 'managed_network'
  | 'video_surveillance'
  | 'access_control'
  | 'pro_av'
  | 'digital_signage'
  | 'security_alarm'
  | 'smart_robotics'
  | 'keytracer'
  | 'it_cloud'
  | 'pos_systems'
  | 'conferencing'
  | 'audio';

/** Display order for solution lists — mirrors the PazNetworks service catalog. */
export const SOLUTION_TYPE_ORDER: readonly SolutionType[] = [
  'managed_network',
  'video_surveillance',
  'access_control',
  'pro_av',
  'digital_signage',
  'security_alarm',
  'smart_robotics',
  'keytracer',
  'it_cloud',
  'pos_systems',
  'conferencing',
  'audio',
];

export function isSolutionType(value: unknown): value is SolutionType {
  return typeof value === 'string' && (SOLUTION_TYPE_ORDER as readonly string[]).includes(value);
}

export const PROPERTY_VISIT_COLUMNS = 'id, property_code, window_label, title, occurred_on, updated_at';

export interface PropertyVisit {
  id: string;
  propertyCode: string;
  /** Curated label as operations writes it: "Apr 1–3, 2026", "Dec 2024". */
  windowLabel: string;
  title: string;
  /** Sortable anchor date (ISO, yyyy-mm-dd). */
  occurredOn: string;
}

/** Raw row shape as PostgREST returns it. */
export interface PropertyVisitRow {
  id: string | null;
  property_code: string | null;
  window_label: string | null;
  title: string | null;
  occurred_on: string | null;
}

export interface PropertyCatalog {
  /** Directory entries the session may see, keyed by canonical code. */
  entries: Map<string, PropertyDirectoryEntry>;
  /** Visits per canonical code, newest first. */
  visits: Map<string, PropertyVisit[]>;
  /** Upper-cased code or alias → canonical code. */
  aliasIndex: Map<string, string>;
}

export const EMPTY_CATALOG: PropertyCatalog = {
  entries: new Map(),
  visits: new Map(),
  aliasIndex: new Map(),
};

export function toVisit(row: PropertyVisitRow): PropertyVisit | null {
  if (!row.id || !row.property_code || !row.title || !row.occurred_on) return null;
  return {
    id: row.id,
    propertyCode: row.property_code,
    // A blank label still sorts and renders: fall back to the anchor date.
    windowLabel: row.window_label?.trim() || row.occurred_on,
    title: row.title,
    occurredOn: row.occurred_on,
  };
}

export function buildPropertyCatalog(
  entries: PropertyDirectoryEntry[],
  visits: PropertyVisit[],
): PropertyCatalog {
  const entryMap = new Map<string, PropertyDirectoryEntry>();
  const aliasIndex = new Map<string, string>();

  for (const entry of entries) {
    entryMap.set(entry.code, entry);
    aliasIndex.set(entry.code.toUpperCase(), entry.code);
    for (const alias of entry.aliases) {
      const key = alias.trim().toUpperCase();
      // A real code always wins over another property's alias.
      if (key && !entryMap.has(key)) aliasIndex.set(key, entry.code);
    }
  }
  // Second pass so a canonical code can never be shadowed by an alias.
  for (const entry of entries) aliasIndex.set(entry.code.toUpperCase(), entry.code);

  const visitMap = new Map<string, PropertyVisit[]>();
  for (const visit of visits) {
    const code = aliasIndex.get(visit.propertyCode.toUpperCase()) ?? visit.propertyCode;
    const list = visitMap.get(code);
    if (list) list.push(visit);
    else visitMap.set(code, [visit]);
  }
  for (const list of visitMap.values()) {
    list.sort((a, b) => b.occurredOn.localeCompare(a.occurredOn) || a.title.localeCompare(b.title));
  }

  return { entries: entryMap, visits: visitMap, aliasIndex };
}

/** Resolve an operations code or alias to its canonical registry code. */
export function resolvePropertyCode(catalog: PropertyCatalog, raw: string | null | undefined): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  return catalog.aliasIndex.get(trimmed.toUpperCase()) ?? null;
}

export function propertyEntry(
  catalog: PropertyCatalog,
  code: string | null | undefined,
): PropertyDirectoryEntry | null {
  const resolved = resolvePropertyCode(catalog, code);
  return resolved ? catalog.entries.get(resolved) ?? null : null;
}

export function visitsForProperty(
  catalog: PropertyCatalog,
  code: string | null | undefined,
): PropertyVisit[] {
  const resolved = resolvePropertyCode(catalog, code);
  return resolved ? catalog.visits.get(resolved) ?? [] : [];
}

/** Caption operations wrote above the visit history, if any. */
export function visitHistoryNote(
  catalog: PropertyCatalog,
  code: string | null | undefined,
): string | null {
  return propertyEntry(catalog, code)?.visit_history_note ?? null;
}

/**
 * The solutions deployed at a property, from its `deployments` rows only, in
 * service-catalog order.
 *
 * There used to be a registry fallback for properties with no rows yet. The
 * owner retired it on 2026-09-21 (#70) once production recorded real
 * deployments for the Stanford properties, so a customer now sees exactly what
 * the deployments table says and nothing a hand-kept list adds. Unknown labels
 * are dropped rather than rendered as raw enum text.
 */
export function listDeployedSolutions(fromDeployments: Iterable<string>): SolutionType[] {
  const known = [...fromDeployments].filter(isSolutionType);
  return [...new Set(known)].sort(
    (a, b) => SOLUTION_TYPE_ORDER.indexOf(a) - SOLUTION_TYPE_ORDER.indexOf(b),
  );
}

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Best-effort match of a free-text account or site name onto a property.
 *
 * Preferred path is always `locations.hierarchy->>'property_code'`, which the
 * registry's own linker maintains. This is the fallback for a name that has not
 * been linked yet, and it only consults the registry: an unmatched name is a
 * missing alias in `property_directory`, fixable without a deploy.
 */
export function findPropertyByName(
  catalog: PropertyCatalog,
  name: string | null | undefined,
): PropertyDirectoryEntry | null {
  const raw = name?.trim();
  if (!raw) return null;

  const byCode = propertyEntry(catalog, raw);
  if (byCode) return byCode;

  const normalized = normalizeName(raw);
  if (!normalized) return null;

  const entries = [...catalog.entries.values()];
  const exact = entries.find((entry) => normalizeName(entry.name) === normalized);
  if (exact) return exact;

  const aliased = entries.find((entry) =>
    entry.aliases.some((alias) => normalizeName(alias) === normalized),
  );
  if (aliased) return aliased;

  // Containment, long names only, so "Hilton" cannot match a dozen properties.
  const contained = entries.find((entry) => {
    const candidate = normalizeName(entry.name);
    if (!candidate || candidate.length < 12) return false;
    return normalized.includes(candidate) || (normalized.length >= 16 && candidate.startsWith(normalized));
  });
  return contained ?? null;
}

/** Codes in the catalog that have at least one recorded visit, sorted. */
export function codesWithVisits(catalog: PropertyCatalog): string[] {
  return [...catalog.visits.entries()]
    .filter(([, list]) => list.length > 0)
    .map(([code]) => code)
    .sort();
}
